// Full-screen onboarding overlay: input lock (four blocking rects around the
// live target, or one full-screen blocker), a pulsing ring, the progress strip
// and the narrator stage (talking-overlay figure + locked mode buttons + caption bubble).
// The lock is built from real Pressables, never a pointerEvents="none" parent
// (that leaks taps on RN-web; see DISCUSSIONS.md "Taps dead").
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Volume2, VolumeX } from 'lucide-react-native';
import { SpeakerFigure, TalkModeButtons } from '../TalkingOverlay';
import { useLinePlayer } from './useLinePlayer';
import { DISPLAY_FONT, makeThemedStyles, useTheme } from '../theme';
import { STOP_LABELS } from './onboardingScript';
import { useOnboarding } from './OnboardingProvider';
import { measureTarget, type Rect } from './targetRegistry';
import type { Speaker } from './types';

const PAD = 6; // spotlight padding around the target
const POLL_MS = 250; // targets sit on a panning canvas, so re-measure while a step is live
const SPEAKER_NAME: Record<Speaker, string> = { syqi: 'SyQi', telos: 'ATLAS', oracle: 'ATLAS', numa: 'POROS', kyrs: 'KYRS', krnos: 'KRNOS', metis: 'METIS', clio: 'CLIO' };

const sameRect = (a: Rect | null, b: Rect | null) =>
  a === b || (!!a && !!b && Math.abs(a.x - b.x) < 1 && Math.abs(a.y - b.y) < 1 && Math.abs(a.w - b.w) < 1 && Math.abs(a.h - b.h) < 1);

const swallow = () => {};

export function TourLayer() {
  const ob = useOnboarding();
  const { t } = useTheme();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();
  const rootRef = useRef<View>(null);
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const [rect, setRect] = useState<Rect | null>(null);
  const pulse = useRef(new Animated.Value(0)).current;

  // Line audio: plays once Begin is tapped; a finished clip auto-advances within the step (the last line waits for the user).
  const lineCount = ob.step?.lines.length ?? 0;
  const player = useLinePlayer({
    lineId: ob.line?.id,
    text: ob.line?.text ?? '',
    enabled: ob.active && ob.begun,
    muted: ob.muted,
    replayTick: ob.replayTick,
    onDone: () => {
      if (ob.lineIdx < lineCount - 1) ob.nextLine();
    },
  });

  const target = ob.liveTarget;
  const active = ob.active && !!ob.step;

  // Pulsing ring.
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, { toValue: 1, duration: 800, easing: Easing.inOut(Easing.quad), useNativeDriver: true }),
        Animated.timing(pulse, { toValue: 0, duration: 800, easing: Easing.inOut(Easing.quad), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  // Track this layer's own window origin and the live target's rect.
  useEffect(() => {
    if (!active) {
      setRect(null);
      return;
    }
    let alive = true;
    const tick = async () => {
      rootRef.current?.measureInWindow((x, y) => {
        if (alive && Number.isFinite(x) && Number.isFinite(y)) setOrigin((o) => (Math.abs(o.x - x) < 1 && Math.abs(o.y - y) < 1 ? o : { x, y }));
      });
      const r = target ? await measureTarget(target, winW, winH) : null;
      if (alive) setRect((prev) => (sameRect(prev, r) ? prev : r));
    };
    void tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [active, target, winW, winH]);

  if (!active || !ob.step) return null;
  const { step } = ob;

  // Target rect in this layer's coordinates, padded and clamped to the window.
  const r = rect
    ? (() => {
        const x0 = Math.max(0, rect.x - PAD);
        const y0 = Math.max(0, rect.y - PAD);
        const x1 = Math.min(winW, rect.x + rect.w + PAD);
        const y1 = Math.min(winH, rect.y + rect.h + PAD);
        return { x: x0 - origin.x, y: y0 - origin.y, w: Math.max(0, x1 - x0), h: Math.max(0, y1 - y0) };
      })()
    : null;

  // Bubble goes to the top when the target is low, so it never covers it.
  const narratorTop = !!r && r.y + r.h / 2 > winH * 0.6;
  const lastStop = step.stop;
  // The figure is front-centre like the talking overlay; fade it (never move it) when the target sits behind it.
  const figH = 0.62 * winH;
  const figW = figH * 0.8;
  const hits = (x: number, y: number, w: number, h: number) => !!r && r.x < x + w && r.x + r.w > x && r.y < y + h && r.y + r.h > y;
  const figureCovers = hits((winW - figW) / 2, winH - figH, figW, figH);
  const buttonsCover = hits(winW - 56, insets.top + 50, 56, 190);
  // Once a deity tool is open the narrator figure and mode buttons step aside; caption + spotlight stay.
  const inTool = step.screen === 'deitytool' || step.screen === 'tool';

  return (
    <View ref={rootRef} style={[StyleSheet.absoluteFill, { zIndex: 1000, elevation: 1000 }]} pointerEvents="box-none">
      {/* INPUT LOCK */}
      {r ? (
        <>
          <Pressable onPress={swallow} accessible={false} style={[styles.block, { left: 0, top: 0, right: 0, height: r.y }]} />
          <Pressable onPress={swallow} accessible={false} style={[styles.block, { left: 0, top: r.y + r.h, right: 0, bottom: 0 }]} />
          <Pressable onPress={swallow} accessible={false} style={[styles.block, { left: 0, top: r.y, width: r.x, height: r.h }]} />
          <Pressable onPress={swallow} accessible={false} style={[styles.block, { left: r.x + r.w, top: r.y, right: 0, height: r.h }]} />
          <Animated.View
            pointerEvents="none"
            style={[
              styles.ring,
              {
                left: r.x,
                top: r.y,
                width: r.w,
                height: r.h,
                borderRadius: Math.min(r.w, r.h) / 2 > 28 ? 22 : Math.min(r.w, r.h) / 2,
                opacity: pulse.interpolate({ inputRange: [0, 1], outputRange: [0.45, 1] }),
                transform: [{ scale: pulse.interpolate({ inputRange: [0, 1], outputRange: [1, 1.06] }) }],
              },
            ]}
          />
        </>
      ) : (
        <Pressable onPress={swallow} accessible={false} style={[styles.block, StyleSheet.absoluteFillObject]} />
      )}

      {/* PROGRESS STRIP (sits between the header's menu and action buttons) */}
      <View pointerEvents="none" style={[styles.progress, { top: insets.top + 6 }]}>
        <Text style={styles.progressTitle} numberOfLines={1}>System setup</Text>
        <View style={styles.dots}>
          {[1, 2, 3, 4, 5].map((n) => (
            <View key={n} style={[styles.dot, n <= lastStop && styles.dotOn, n === lastStop && styles.dotNow]} />
          ))}
        </View>
        <Text style={styles.progressStop} numberOfLines={1}>
          {lastStop === 0 ? STOP_LABELS[0] : `Stop ${lastStop} of 5 · ${STOP_LABELS[lastStop]}`}
        </Text>
      </View>

      {/* NARRATOR STAGE: the talking overlay's figure and mode buttons (locked), plus caption + controls. */}
      {!inTool ? (
        <>
      <View pointerEvents="none" style={[styles.figure, { opacity: figureCovers ? 0.3 : 1 }]}>
        <SpeakerFigure deityId={step.speaker} hallOnly keepPoster audioTrack={player.track} speaking={player.speaking} />
      </View>
      <View pointerEvents={buttonsCover ? 'none' : 'box-none'} style={[StyleSheet.absoluteFill, { opacity: buttonsCover ? 0.25 : 1 }]}>
        <TalkModeButtons displayName={SPEAKER_NAME[step.speaker]} mode="video" disabled onClose={swallow} onAudio={swallow} onChat={swallow} onVideo={swallow} />
      </View>
        </>
      ) : null}
      {!ob.begun ? (
        <View pointerEvents="box-none" style={[StyleSheet.absoluteFill, styles.beginWrap]}>
          <Pressable onPress={ob.begin} accessibilityLabel="Begin" style={styles.beginBtn}>
            <Text style={styles.beginText}>Begin</Text>
          </Pressable>
        </View>
      ) : null}
      <View style={[styles.bubble, narratorTop ? { top: insets.top + 56 } : { bottom: insets.bottom + 14 }]}>
        <Text style={styles.who}>{SPEAKER_NAME[step.speaker]}</Text>
        <Text style={styles.caption}>{ob.line?.text ?? ''}</Text>
        <View style={styles.controls}>
          <Pressable onPress={ob.replayLine} hitSlop={8} accessibilityLabel="Replay line" style={styles.ctl}>
            <Text style={styles.ctlGlyph}>{'\u21BB'}</Text>
          </Pressable>
          {!ob.linesDone || !step.waitFor ? (
            <Pressable onPress={ob.nextLine} hitSlop={8} accessibilityLabel="Next line" style={[styles.ctl, styles.ctlPrimary]}>
              <Text style={[styles.ctlGlyph, { color: t.onGold }]}>{'\u25B6'}</Text>
            </Pressable>
          ) : (
            <Text style={styles.hint}>Your turn</Text>
          )}
          <Pressable onPress={ob.toggleMute} hitSlop={8} accessibilityLabel={ob.muted ? 'Unmute narration' : 'Mute narration'} style={styles.ctl}>
            {ob.muted ? <VolumeX size={16} color={t.goldBright} /> : <Volume2 size={16} color={t.goldBright} />}
          </Pressable>
          <View style={{ flex: 1 }} />
          <Pressable onPress={ob.skip} hitSlop={8}>
            <Text style={styles.skip}>Skip setup</Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    block: { position: 'absolute', backgroundColor: 'rgba(4,8,18,0.62)' },
    ring: { position: 'absolute', backgroundColor: 'transparent', boxShadow: `0 0 0 3px ${t.goldBright}, 0 0 22px ${t.goldGlow}` } as object,
    progress: { position: 'absolute', left: 66, right: 112, alignItems: 'center', gap: 3, paddingVertical: 4 },
    progressTitle: { color: t.ivory, fontFamily: DISPLAY_FONT, fontSize: 12, letterSpacing: 1.5 },
    dots: { flexDirection: 'row', gap: 6 },
    dot: { width: 9, height: 9, borderRadius: 5, backgroundColor: t.track },
    dotOn: { backgroundColor: t.gold },
    dotNow: { boxShadow: `0 0 8px ${t.goldGlow}` } as object,
    progressStop: { color: t.ivorySoft, fontSize: 10, fontWeight: '700', letterSpacing: 0.6 },
    beginWrap: { alignItems: 'center', justifyContent: 'center', zIndex: 5 },
    beginBtn: { paddingHorizontal: 44, paddingVertical: 16, borderRadius: 999, backgroundColor: t.gold, boxShadow: `0 10px 28px ${t.glowSoft}, 0 0 22px ${t.goldGlow}` } as object,
    beginText: { color: t.onGold, fontFamily: DISPLAY_FONT, fontSize: 20, letterSpacing: 2 },
    figure: { position: 'absolute', left: 0, right: 0, bottom: 0, alignItems: 'center', justifyContent: 'flex-end', zIndex: 1 },
    bubble: { position: 'absolute', left: 12, right: 60, borderRadius: 20, paddingHorizontal: 14, paddingVertical: 10, gap: 6, backgroundColor: t.navyRaised, boxShadow: `0 8px 22px ${t.glowSoft}` } as object,
    who: { color: t.goldBright, fontSize: 10, fontWeight: '800', letterSpacing: 1.4 },
    caption: { color: t.ivory, fontSize: 14, lineHeight: 20 },
    controls: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    ctl: { width: 34, height: 34, borderRadius: 17, alignItems: 'center', justifyContent: 'center', backgroundColor: t.glass },
    ctlPrimary: { backgroundColor: t.gold },
    ctlGlyph: { color: t.goldBright, fontSize: 14, fontWeight: '800' },
    hint: { color: t.goldBright, fontSize: 12, fontWeight: '800', letterSpacing: 0.6 },
    skip: { color: t.ivoryFaint, fontSize: 11, fontWeight: '700', textDecorationLine: 'underline' },
  }),
);
